import { useState } from 'react'

import { getOnlyDate } from '~/astra'
import { PractitionerScheduleCreate } from '~/clients/derived-api-types'
import { createPractitionerSchedule, updatePractitionerSchedule } from '~/store/di-entity.api'
import { PractitionerSchedule, usePractitionerSchedule } from '~/storm'
import { trackUserEvent } from '~/utils/analytics'
import { getNonEmptyString } from '~/utils/utils'

import { DatePractitionerCell, Note } from '../../shared'

async function upsertNote(selectedCell: DatePractitionerCell, practitionerSchedule: PractitionerSchedule | undefined, newNote?: string) {
    const scheduleData: PractitionerScheduleCreate = practitionerSchedule
        ? {
              practitioner_id: practitionerSchedule.practitioner_id,
              scheduled_date: practitionerSchedule.scheduled_date,
              comment: newNote,
          }
        : {
              practitioner_id: selectedCell.practitionerId,
              scheduled_date: getOnlyDate(selectedCell.date),
              comment: newNote,
          }
    if (practitionerSchedule && practitionerSchedule.comment === newNote) return
    return practitionerSchedule ? updatePractitionerSchedule(practitionerSchedule.id, scheduleData) : createPractitionerSchedule(scheduleData)
}

type Props = {
    cell: DatePractitionerCell
    hideNote: () => void
}

export const PractitionerNote = ({ cell, hideNote }: Props) => {
    const practitionerSchedule = usePractitionerSchedule({
        where: { scheduled_date: getOnlyDate(cell.date), practitioner_id: cell.practitionerId },
    })

    const [note, setNote] = useState(practitionerSchedule?.comment ?? '')
    const [isLoading, setIsLoading] = useState(false)

    async function updateNote() {
        const newNote = getNonEmptyString(note)
        if (newNote === practitionerSchedule?.comment) {
            hideNote()
            return
        }

        setIsLoading(true)

        try {
            await upsertNote(cell, practitionerSchedule, newNote)
        } catch (error) {
            console.error(error)
            return
        } finally {
            setIsLoading(false)
            setNote(newNote ?? '')
            hideNote()
        }

        void trackUserEvent('SURGEON_NOTE_SAVED')
    }

    function undoNote() {
        setNote(practitionerSchedule?.comment ?? '')
        hideNote()
    }

    async function deleteNote() {
        if (!practitionerSchedule) return

        setIsLoading(true)

        try {
            await upsertNote(cell, practitionerSchedule, undefined)
        } catch (error) {
            console.error(error)
            return
        } finally {
            setIsLoading(false)
            setNote('')
            hideNote()
        }

        void trackUserEvent('SURGEON_NOTE_REMOVED')
    }

    return <Note note={note} setNote={setNote} updateNote={updateNote} deleteNote={deleteNote} undoNote={undoNote} disabled={isLoading} />
}
